import { TDirtyFields } from '@/shared/components/ui/form/form.types';

/**
 * Recursively filters an object to only include properties that are marked as "dirty" (changed)
 * @param data The complete form data object
 * @param dirtyFields Object containing flags for which fields are dirty
 * @returns New object containing only the properties that were changed, including nested objects
 */
export const filterDirtyFields = <T extends Record<string, any>>(
  data: T,
  dirtyFields: TDirtyFields<T>,
): Partial<T> => {
  const changedData: Partial<T> = {};

  if (!data) {
    return changedData;
  }

  for (const key in dirtyFields) {
    if (Object.prototype.hasOwnProperty.call(dirtyFields, key)) {
      if (dirtyFields[key] === true) {
        changedData[key as keyof T] = data[key as keyof T];
      } else if (Array.isArray(dirtyFields[key])) {
        // RHF marks dirty arrays as arrays of flags; recursing would collapse
        // them into objects with numeric keys, so send the whole array instead.
        changedData[key as keyof T] = data[key as keyof T];
      } else if (
        dirtyFields[key] !== false &&
        typeof dirtyFields[key] === 'object' &&
        dirtyFields[key] !== null
      ) {
        const nestedResult = filterDirtyFields(
          data[key as keyof T],
          dirtyFields[key] as TDirtyFields<any>,
        );

        if (Object.keys(nestedResult).length > 0) {
          changedData[key as keyof T] = nestedResult as any;
        }
      }
    }
  }

  return changedData;
};
